import { Vector2 } from "../../common/vector2.js";
import { InviteService } from "../../friends/invite_service.js";
import { ImgID } from "../../image_loader/img_id.js";
import { DialogManager } from "../dialog/dialog_manager.js";
import { InviteFriendDialog } from "../dialog/invite_friend_dialog.js";
import { ColorScheme } from "./color_scheme.js";
import { TextStyleScheme } from "./text_style_scheme.js";
import { UIElementHolder } from "./ui_element_holder.js";
import { UIImage } from "./ui_image.js";
import { UINineSliceElement } from "./ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "./ui_text_element.js";

export class InviteFriendBlock extends UIElementHolder
{
  public static readonly DESIGN_W = 987;
  public static readonly DESIGN_H = 294;
  private static readonly DEFAULT_REWARD_COINS = 5000;

  private reward_text: UITextElement | null = null;
  private coins: UIImage | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    private readonly on_invite: () => void = () => DialogManager.openDialog( new InviteFriendDialog() )
  )
  {
    const block_w = LayoutScaler.widthFromDesign( InviteFriendBlock.DESIGN_W );
    const block_h = LayoutScaler.heightFromDesign( InviteFriendBlock.DESIGN_H );

    super( ctx, x, y, block_w, block_h );

    const bg = new UINineSliceElement( ctx, 0, 0, block_w, block_h, ImgID.UI_CapsuleBlue, "capsule_bg" );
    this.addChild( bg );

    this.createTitle( bg, block_w );
    this.createRewardRow( bg );

    void InviteService.refresh().then( summary =>
    {
      if ( summary )
        this.reward_text?.setText( `+${summary.reward_coins}` );
    } );
  }

  public name(): string
  {
    return "InviteFriendBlock";
  }

  public coinsCenter(): Vector2
  {
    const rect = this.coins?.getRect();
    const origin = this.coins?.getAbsolutePosition() ?? this.getAbsolutePosition();
    const w = rect?.getW() ?? 0;
    const h = rect?.getH() ?? 0;

    return new Vector2( origin.x + w / 2, origin.y + h / 2 );
  }

  public coinsSize(): number
  {
    return this.coins?.getRect().getW() ?? 0;
  }

  private createTitle( bg: UINineSliceElement, block_w: number ): void
  {
    const title_y = LayoutScaler.yFromDesign( 45 );
    const title_h = LayoutScaler.heightFromDesign( 70 );

    const title = new UITextElement(
      this.ctx,
      0,
      title_y,
      block_w,
      title_h,
      {
        localization_key: "INVITE_FRIEND.TEXT",
        font_size: LayoutScaler.textSizeFromDesign( 55 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    title.setClickable( false );
    bg.addChild( title );
  }

  private createRewardRow( bg: UINineSliceElement ): void
  {
    const reward_x = LayoutScaler.xFromDesign( 45 );
    const reward_y = LayoutScaler.yFromDesign( 150 );
    const reward_w = LayoutScaler.widthFromDesign( 360 );
    const reward_h = LayoutScaler.heightFromDesign( 100 );
    const row_center_y = reward_y + reward_h / 2;

    const reward = new UITextElement(
      this.ctx,
      reward_x,
      reward_y,
      reward_w,
      reward_h,
      {
        text: `+${InviteService.get()?.reward_coins ?? InviteFriendBlock.DEFAULT_REWARD_COINS}`,
        font_size: LayoutScaler.textSizeFromDesign( 90 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.friends_invite_reward,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        outline_color: ColorScheme.friends_invite_reward_outline,
        auto_wrap: false,
      }
    );

    reward.setClickable( false );
    bg.addChild( reward );
    this.reward_text = reward;

    const coins_w = LayoutScaler.widthFromDesign( 195 );
    const coins_h = LayoutScaler.heightFromDesign( 195 );
    const coins_x = LayoutScaler.xFromDesign( 470 ) - coins_w / 2;
    const coins_y = row_center_y - coins_h / 2 - LayoutScaler.yFromDesign( 10 );

    const coins = new UIImage( this.ctx, coins_x, coins_y, coins_w, coins_h, ImgID.UI_ShopBlockImg );

    coins.setClickable( false );
    bg.addChild( coins );
    this.coins = coins;

    const button_w = LayoutScaler.widthFromDesign( 334 );
    const button_h = LayoutScaler.heightFromDesign( 134 );
    const button_x = LayoutScaler.xFromDesign( 756 ) - button_w / 2;
    const button_y = row_center_y - button_h / 2;

    const button = new UIImage( this.ctx, button_x, button_y, button_w, button_h, ImgID.UI_GreenFrameBtn, {}, true );

    const button_text = new UITextElement(
      this.ctx,
      0,
      0,
      button_w,
      button_h,
      {
        localization_key: "INVITE_FRIEND.BUTTON",
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.menu_button_outline,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    button.addChild( button_text, true );
    button.setOnClick( () => this.on_invite() );
    bg.addChild( button );
  }
}
